import { z } from "zod/v4";
import { type ExpressionBuilder, type Transaction, type QueryRowType } from "@rocicorp/zero";
import { zql, type Schema } from "../../zero/schema";
import { defineQuery } from "../../zero/transformer";
import { withZeroTransaction } from "../../zero/utils";
import { createInsertSchema } from "../../utils/sql";
import { temporalPlainDateSchema } from "../../common/date-time/zod";
import { bodyCharacteristic } from "./body-characteristic.sql";
import { Result, safeParseInt } from "@corpuscomplete/core/utils";
import { AccessGrant } from "@corpuscomplete/core/auth/grant";
import type { UserActor } from "@corpuscomplete/core/actor";

export namespace BodyCharacteristic {
  export const forUser = defineQuery(
    z.object({ userId: z.uuid() }),
    ({ ctx, args: { userId } }) => {
      const q = zql.bodyCharacteristic.where("userId", "=", userId).one();
      if (userId === ctx.actor.userId) {
        return q;
      }
      return q.where((e) => viewable(e, ctx.actor));
    },
  );
  export type Info = NonNullable<QueryRowType<typeof forUser>>;

  function viewable(eb: ExpressionBuilder<"bodyCharacteristic", Schema>, actor: UserActor) {
    return eb.or(
      eb.cmp("userId", "=", actor.userId),
      eb.exists("grantsByUser", (q) =>
        AccessGrant.matching(
          q,
          actor,
          AccessGrant.criteria("user", "body_characteristics", ["editor", "viewer"]),
        ),
      ),
    );
  }

  export type BiologicalSex = NonNullable<Info["biologicalSex"]>;
  // Can't figure out a convenient way to get the schema from the DB, so just using a Zod enum for now
  export const biologicalSexSchema = z.enum(["male", "female", "other"]);
  export const biologicalSexOptions: { label: string; value: BiologicalSex }[] = [
    { label: "Male", value: "male" },
    { label: "Female", value: "female" },
    { label: "Other", value: "other" },
  ];

  async function canEdit(tx: Transaction, actor: UserActor, userId: string): Promise<boolean> {
    if (actor.userId === userId) {
      return true;
    }
    const result = await tx.run(
      AccessGrant.matching(
        zql.accessGrant.where("resourceId", "=", userId),
        actor,
        AccessGrant.criteria("user", "body_characteristics", ["editor"]),
      ).one(),
    );
    return !!result;
  }

  export type Set = z.infer<typeof set.schema>;
  export const set = withZeroTransaction(
    createInsertSchema(bodyCharacteristic).extend({
      dateOfBirth: temporalPlainDateSchema.nullable(),
    }),
    async ({ tx, ctx, args: { biologicalSex, dateOfBirth, userId } }) => {
      const hasPermission = await canEdit(tx, ctx.actor, userId);
      if (!hasPermission) {
        throw new Error(
          "You do not have permission to edit the body characteristics for this user",
        );
      }
      const existing = await tx.run(zql.bodyCharacteristic.where("userId", "=", userId).one());
      const timeNow = Temporal.Now.instant();
      if (existing) {
        await tx.mutate.bodyCharacteristic.update({
          userId,
          biologicalSex,
          dateOfBirth,
          timeUpdated: timeNow,
        });
      } else {
        await tx.mutate.bodyCharacteristic.insert({
          userId,
          biologicalSex,
          dateOfBirth,
          timeCreated: timeNow,
          timeUpdated: timeNow,
        });
      }
    },
  );

  export type DateOfBirthForm = z.infer<ReturnType<typeof dateOfBirthFormSchema>>;
  export const dateOfBirthFormDefault = (): DateOfBirthForm => ({
    year: "",
    month: "",
    day: "",
  });
  export const dateOfBirthFormSchema = (opts?: { ageRange?: Range; optional?: boolean }) =>
    z
      .object({
        year: z.string(),
        month: z.string(),
        day: z.string(),
      })
      .check((ctx) => {
        const optional = opts?.optional ?? false;
        if (optional && ctx.value.year === "" && ctx.value.month === "" && ctx.value.day === "") {
          return;
        }
        const dateOfBirth = dateOfBirthFromForm(ctx.value);
        if (dateOfBirth === null) {
          ctx.issues.push({
            code: "custom",
            message: "Must be a valid date",
            input: ctx.value,
          });
          return;
        }

        const ageResult = dateOfBirthToAge(dateOfBirth, opts?.ageRange);
        if (!ageResult.ok) {
          ctx.issues.push({
            code: "custom",
            message: ageResult.error,
            input: ctx.value,
          });
        }
      });
  export const dateOfBirthFromForm = (form: DateOfBirthForm): Temporal.PlainDate | null => {
    try {
      const year = safeParseInt(form.year);
      const month = safeParseInt(form.month);
      const day = safeParseInt(form.day);
      if (year === null && month === null && day === null) {
        return null;
      }
      if (year !== null && month !== null && day !== null) {
        const result = Temporal.PlainDate.from(
          {
            year,
            month,
            day,
          },
          {
            overflow: "reject",
          },
        );
        return result;
      }
      return null;
    } catch {
      return null;
    }
  };

  type Range = { min: number; max: number };

  export const dateOfBirthToAge = (
    dateOfBirth: Temporal.PlainDate,
    range?: Range,
  ): Result<number, string> => {
    const minAge = range?.min ?? 0;
    const maxAge = range?.max ?? 150;
    const now = Temporal.Now.plainDateISO();
    const value = now.since(dateOfBirth).round({ smallestUnit: "years", relativeTo: now }).years;
    if (value < minAge) {
      return { ok: false, error: `Must be at least ${minAge} years old` };
    }
    if (value > maxAge) {
      return { ok: false, error: `Must be at most ${maxAge} years old` };
    }
    return { ok: true, value };
  };

  export const formSchema = z.object({
    biologicalSex: biologicalSexSchema.nullable(),
    dateOfBirth: dateOfBirthFormSchema({ optional: true }),
  });
  export type Form = z.infer<typeof formSchema>;
  export const formDefault = (data: Info | undefined): Form => {
    let dateOfBirth = dateOfBirthFormDefault();
    const dateOfBirthValue = data?.dateOfBirth ?? null;
    if (dateOfBirthValue) {
      if (!(dateOfBirthValue instanceof Temporal.PlainDate)) {
        throw new TypeError("Expected bodyCharacteristic.dateOfBirth to be a Temporal.PlainDate");
      }

      dateOfBirth = {
        year: dateOfBirthValue.year.toString(),
        month: dateOfBirthValue.month.toString(),
        day: dateOfBirthValue.day.toString(),
      };
    }
    return {
      biologicalSex: data?.biologicalSex ?? null,
      dateOfBirth,
    };
  };
  export const formToSet = (userId: string, form: Form): Set => {
    return {
      userId,
      biologicalSex: form.biologicalSex,
      dateOfBirth: dateOfBirthFromForm(form.dateOfBirth),
    };
  };
}
